import { Controller } from "@hotwired/stimulus"

// Phase 3.7 Tier 6 — admin: partner support ticket triage.
//
// Mirrors integration_admin_requests_controller — per-row status
// change saves immediately, bulk select bar appears once a row is
// checked, notes modal edits internal_notes inline. Replies live on
// the per-ticket detail page (the form posts to
// IntegrationAdmin::PartnerTicketCommentsController#create).
export default class extends Controller {
  static targets = [
    "rowCheckbox", "allCheckbox",
    "bulkBar", "bulkCount",
    "notesModal", "notesField", "notesSubject"
  ]
  static values = {
    updateUrlTemplate: String,
    bulkUrl: String
  }

  toggleAll(event) {
    const checked = event.currentTarget.checked
    this.rowCheckboxTargets.forEach(c => { c.checked = checked })
    this._refreshBulkBar()
  }

  toggleRow() { this._refreshBulkBar() }

  _refreshBulkBar() {
    const ids = this._selectedIds()
    if (ids.length === 0) {
      this.bulkBarTarget.classList.add("hidden")
    } else {
      this.bulkBarTarget.classList.remove("hidden")
      if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = ids.length
    }
  }

  _selectedIds() {
    return this.rowCheckboxTargets.filter(c => c.checked).map(c => c.value)
  }

  async bulkUpdate(event) {
    const ids = this._selectedIds()
    if (ids.length === 0) return
    const body = new FormData()
    body.append("status", event.currentTarget.dataset.status)
    ids.forEach(id => body.append("ticket_ids[]", id))
    try {
      const res = await fetch(this.bulkUrlValue, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) this._flashAndReload(data.message || "Updated.", "notice")
      else this._toast(data.message || "Couldn't update.", "alert")
    } catch (_) {
      this._toast("Network error — try again.", "alert")
    }
  }

  async changeStatus(event) {
    const id = event.currentTarget.dataset.id
    const url = this.updateUrlTemplateValue.replace("ID", id)
    const body = new FormData()
    body.append("partner_support_ticket[status]", event.currentTarget.value)
    await this._patch(url, body, "Status saved.")
  }

  openNotes(event) {
    this._notesId = event.currentTarget.dataset.id
    if (this.hasNotesFieldTarget) this.notesFieldTarget.value = event.currentTarget.dataset.notes || ""
    if (this.hasNotesSubjectTarget) this.notesSubjectTarget.textContent = event.currentTarget.dataset.subject || ""
    this.notesModalTarget.classList.remove("hidden")
  }

  closeNotes() {
    this.notesModalTarget.classList.add("hidden")
    this._notesId = null
  }

  stopPropagation(event) { event.stopPropagation() }

  async saveNotes() {
    if (!this._notesId) return
    const url = this.updateUrlTemplateValue.replace("ID", this._notesId)
    const body = new FormData()
    body.append("partner_support_ticket[internal_notes]", this.notesFieldTarget.value)
    const ok = await this._patch(url, body, "Notes saved.")
    if (ok) this.closeNotes()
  }

  async _patch(url, body, message) {
    try {
      const res = await fetch(url, {
        method: "PATCH",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" }
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok && data.ok) {
        this._toast(message || data.message || "Saved.", "notice")
        return true
      }
      this._toast(data.message || "Couldn't save.", "alert")
      return false
    } catch (_) {
      this._toast("Network error — try again.", "alert")
      return false
    }
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _toast(message, type) {
    const div = document.createElement("div")
    div.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    div.dataset.controller = "flash"
    div.dataset.flashDismissAfterValue = "5000"
    div.textContent = message
    document.body.prepend(div)
  }

  _flashAndReload(message, type) {
    try {
      sessionStorage.setItem("integration-admin.flash",
        JSON.stringify({ message, type, ts: Date.now() }))
    } catch (_) {}
    window.location.reload()
  }
}
